import type { RouteObject } from 'react-router-dom'
import { Navigate, Outlet, useRoutes } from 'react-router-dom'

import {
  BUSINESS_ROLES,
  COSTS_ROLES,
  ERASURE_ROLES,
  KYC_REVIEW_ACCESS,
  P2P_ROLES,
  RETURN_AREA_FEATURE,
  RETURN_AREA_ROLES,
  VEHICLE_EDIT_ACCESS,
} from 'src/access'
// ProgressBar behaves weirdly if LoginPage is Loadable
import { LoginPage } from 'src/pages/LoginPage'

import AppleOauthPage from 'src/pages/AppleOauth/AppleOauthPage'
import GoogleOauthPage from 'src/pages/GoogleOauth/GoogleOauthPage'
import InvitationPage from 'src/pages/InvitationPage/InvitationPage'
import PasswordForgotPage from 'src/pages/PasswordForgotPage/PasswordForgotPage'
import PasswordResetPage from 'src/pages/PasswordResetPage/PasswordResetPage'
import TeslaOauthPage from 'src/pages/TeslaOauth/TelslaOauthPage'
import ProtectedRoute from '../layouts/ProtectedRoute'
import Loadable from './Loadable'
import { lazyScreen as lazy } from './lazyScreen'

const AppLayout = Loadable(
  lazy(() => import('../layouts/AppLayout')),
  {
    fallback: 'app',
  },
)

const LogoOnlyLayout = Loadable(
  lazy(() => import('src/layouts/LogoOnlyLayout')),
  { fallback: 'app' },
)

const OnboardingPage = Loadable(
  lazy(() => import('src/pages/OnboardingPage/OnboardingPage')),
)

const ModeSelectionPage = Loadable(
  lazy(() => import('src/pages/ModeSelectionPage/ModeSelectionPage')),
)

const StripeConnectPage = Loadable(
  lazy(() => import('src/pages/StripeConnectPage/StripeConnectPage')),
)

const BusinessPage = Loadable(
  lazy(() => import('src/pages/Business/BusinessPage')),
)
const CostsPage = Loadable(lazy(() => import('src/pages/Costs/CostsPage')))
const CostVendorPage = Loadable(
  lazy(() => import('src/pages/Costs/CostVendorPage')),
)
const AmortisationPage = Loadable(
  lazy(() => import('src/pages/Amortisation/AmortisationPage')),
)
const AnalyticsPage = Loadable(
  lazy(() => import('src/pages/Analytics/AnalyticsPage')),
)

const BookingLocationsPage = Loadable(
  lazy(() => import('src/pages/BookingLocationsPage/BookingLocationsPage')),
)
const ReturnAreasPage = Loadable(
  lazy(() => import('src/pages/ReturnAreasPage/ReturnAreasPage')),
)

const TagsPage = Loadable(lazy(() => import('src/pages/TagsPage/TagsPage')))
const ChargingPage = Loadable(
  lazy(() => import('src/pages/ChargingPage/ChargingPage')),
)

const PromoCodesPage = Loadable(
  lazy(() => import('src/pages/PromoCodesPage/PromoCodesPage')),
)

const CurrentRentals = Loadable(
  lazy(() => import('src/pages/CurrentRentals/CurrentRentals')),
)

const HistoryRentals = Loadable(
  lazy(() => import('src/pages/HistoryRentals/HistoryRentals')),
)

const OpsVehicleTaskPage = Loadable(
  lazy(
    () => import('src/pages/OpsTasks/OpsVehicleTaskPage/OpsVehicleTaskPage'),
  ),
)

const OpsVehicleTasksListPage = Loadable(
  lazy(
    () =>
      import(
        'src/pages/OpsTasks/OpsVehicleTasksListPage/OpsVehicleTasksListPage'
      ),
  ),
)

const PartnerListPage = Loadable(
  lazy(() => import('src/pages/Partner/PartnerListPage/PartnerListPage')),
)

const PartnerPage = Loadable(
  lazy(() => import('src/pages/Partner/PartnerPage/PartnerPage')),
)

const PartnerPricingTiersPage = Loadable(
  lazy(
    () =>
      import(
        'src/pages/Partner/PartnerPricingTiersPage/PartnerPricingTiersPage'
      ),
  ),
)

const VehicleApplicationDetailsPage = Loadable(
  lazy(
    () =>
      import(
        'src/pages/Partner/VehicleApplicationDetailPage/VehicleApplicationDetailsPage'
      ),
  ),
)

const VehicleApplicationListPage = Loadable(
  lazy(
    () =>
      import(
        'src/pages/Partner/VehicleApplicationListPage/VehicleApplicationListPage'
      ),
  ),
)

const PricingPage = Loadable(
  lazy(() => import('src/pages/PricingPage/PricingPage')),
)

const RentalTermsPage = Loadable(
  lazy(() => import('src/pages/RentalTermsPage/RentalTermsPage')),
)

const RentalDetailPage = Loadable(
  lazy(() => import('src/pages/RentalDetailPage/RentalDetailsPage')),
)

const RentalsFeedbackPage = Loadable(
  lazy(() => import('src/pages/RentalsFeedbackPage/RentalsFeedbackPage')),
)

const ReportExportsPage = Loadable(
  lazy(() => import('src/pages/ReportExportsPage/ReportExportsPage')),
)

const KycReviewsPage = Loadable(
  lazy(() => import('src/pages/KycReviewsPage/KycReviewsPage')),
)
const KycReviewPage = Loadable(
  lazy(() => import('src/pages/KycReviewPage/KycReviewPage')),
)
const UserDeleteRequestPage = Loadable(
  lazy(() => import('src/pages/UserDeleteRequestPage/UserDeleteRequestPage')),
)

const UserDetailPage = Loadable(
  lazy(() => import('src/pages/UserDetailPage/UserDetailPage')),
)

const UserListPage = Loadable(
  lazy(() => import('src/pages/UserListPage/UserListPage')),
)

const VehicleCreateEditPage = Loadable(
  lazy(() => import('src/pages/VehicleCreateEditPage/VehicleCreateEditPage')),
)

const VehicleDetailsPage = Loadable(
  lazy(() => import('src/pages/VehicleDetailPage/VehicleDetailsPage')),
)

const VehicleListPage = Loadable(
  lazy(() => import('src/pages/VehicleListPage/VehicleListPage')),
)

const VehicleImportPage = Loadable(
  lazy(() => import('src/pages/VehicleImportPage/VehicleImportPage')),
)

const VehicleIncompleteListPage = Loadable(
  lazy(
    () =>
      import('src/pages/VehicleIncompleteListPage/VehicleIncompleteListPage'),
  ),
)

const VehiclePicturesPage = Loadable(
  lazy(() => import('src/pages/VehiclePicturesPage/VehiclePicturesPage')),
)

const MarketingCampaignsPage = Loadable(
  lazy(() => import('src/pages/MarketingCampaigns/MarketingCampaignsPage')),
)

const AppConfigPage = Loadable(
  lazy(() => import('src/pages/AppConfig/AppConfigPage')),
)

const BookingCalendar = Loadable(
  lazy(() => import('src/pages/BookingCalendar/Calendar')),
)

const VehicleMapPage = Loadable(
  lazy(() => import('../pages/VehicleMap/VehicleMapPage')),
)

const LogoutPage = Loadable(lazy(() => import('src/pages/LogoutPage')))

const PlanPage = Loadable(lazy(() => import('src/pages/MyPlanPage/MyPlanPage')))

const SetupPage = Loadable(lazy(() => import('src/pages/SetupPage/SetupPage')))

const TeslaConnectionPage = Loadable(
  lazy(() => import('src/pages/TeslaConnectionPage/TeslaConnectionPage')),
)

const AccountSettingsPage = Loadable(
  lazy(() => import('src/pages/AccountSettingsPage/AccountSettingsPage')),
)

const EmailPreviewsPage = Loadable(
  lazy(() => import('src/pages/EmailPreviewsPage/EmailPreviewsPage')),
)

const NotFound = Loadable(lazy(() => import('src/pages/Page404')))
const Forbidden = Loadable(lazy(() => import('src/pages/Page403')))

const BusinessAccountsListPage = Loadable(
  lazy(
    () => import('../pages/BusinessAccountsListPage/BusinessAccountsListPage'),
  ),
)

const BusinessAccountDetailsPage = Loadable(
  lazy(
    () =>
      import('../pages/BusinessAccountDetailPage/BusinessAccountDetailPage'),
  ),
)

const SubscriptionsListPage = Loadable(
  lazy(() => import('../pages/SubscriptionsListPage/SubscriptionsListPage')),
)

const VehicleKindsListPage = Loadable(
  lazy(() => import('../pages/VehicleKindsListPage/VehicleKindsListPage')),
)

const VehicleKindDetailPage = Loadable(
  lazy(() => import('../pages/VehicleKindsDetailPage/VehicleKindsDetailPage')),
)

const VehicleKindCreatePage = Loadable(
  lazy(() => import('../pages/VehicleKindsCreatePage/VehicleKindsCreatePage')),
)

const SubscriptionPage = Loadable(
  lazy(() => import('../pages/SubscriptionPage/SubscriptionPage')),
)

const ManagersPage = Loadable(
  lazy(() => import('../pages/ManagersPage/ManagersPage')),
)

/**
 * Every screen the backoffice can show, and the only place they are declared.
 *
 * Exported rather than inlined into `useRoutes` so a test can walk it. The
 * preview route manifest in `src/agentPreview/routes.ts` names the screens a
 * sweep opens, and `routes.test.ts` compares the two: a screen added here
 * without a manifest entry fails the build rather than quietly dropping out of
 * every screenshot run.
 */
export const routeConfig: RouteObject[] = [
  {
    path: 'login',
    children: [
      {
        path: '',
        element: <LoginPage />,
      },
      {
        path: 'tesla',
        children: [
          {
            path: 'callback',
            element: <TeslaOauthPage />,
          },
        ],
      },
      {
        path: 'google',
        children: [
          {
            path: 'callback',
            element: <GoogleOauthPage />,
          },
        ],
      },
      {
        // Where the API sends the browser once Apple has posted its answer.
        path: 'apple',
        children: [
          {
            path: 'callback',
            element: <AppleOauthPage />,
          },
        ],
      },
      {
        // Under `/login` so `AuthProvider` does not send a signed-out visitor
        // away from it: the person opening an invitation often has no account.
        path: 'invite',
        element: <InvitationPage />,
      },
      {
        // Both under `/login` for the same reason: whoever opens them cannot
        // sign in, which is why they are here.
        path: 'forgot',
        element: <PasswordForgotPage />,
      },
      {
        path: 'reset',
        element: <PasswordResetPage />,
      },
    ],
  },
  {
    path: 'onboarding',
    children: [
      {
        path: '',
        element: <OnboardingPage />,
      },
    ],
  },
  {
    path: 'preferred-mode',
    children: [
      {
        path: '',
        element: <ModeSelectionPage />,
      },
    ],
  },
  {
    path: 'admin',
    element: <AppLayout />,
    children: [
      {
        path: '',
        element: <Navigate to="/admin/map" replace />,
      },
      {
        path: 'stripe',
        children: [
          {
            path: 'connect',
            element: <StripeConnectPage />,
          },
        ],
      },
      {
        path: 'account',
        children: [
          {
            path: 'plan',
            element: <PlanPage />,
          },
          {
            path: 'settings',
            element: <AccountSettingsPage />,
          },
          {
            path: 'setup',
            element: <SetupPage />,
          },
          {
            path: 'tesla',
            element: <TeslaConnectionPage />,
          },
          {
            path: 'email-previews',
            element: <EmailPreviewsPage />,
          },
        ],
      },
      {
        path: 'vehicles',
        children: [
          {
            path: '',
            element: <VehicleListPage />,
          },
          {
            path: 'create',
            element: (
              <ProtectedRoute
                requiredFeatureFlag="vehicleCreateFormEnabled"
                requiredRole={['admin', 'fleetManager']}
              >
                <VehicleCreateEditPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'import',
            element: <VehicleImportPage />,
          },
          {
            path: 'update-incomplete',
            element: <VehicleIncompleteListPage />,
          },
          {
            path: ':id',
            children: [
              {
                path: '',
                element: <VehicleDetailsPage />,
              },
              {
                path: 'edit',
                element: (
                  // Editing has its own flag. This route asked for the
                  // creation flag, which refused every provider whose plan
                  // includes editing and not adding cars.
                  <ProtectedRoute {...VEHICLE_EDIT_ACCESS}>
                    <VehicleCreateEditPage isEditing />
                  </ProtectedRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'charging',
        element: <ChargingPage />,
      },
      {
        path: 'rentals',
        children: [
          {
            path: '',
            element: <Navigate to="/admin/rentals/current" replace />,
          },
          {
            path: 'current',
            element: <CurrentRentals />,
          },
          {
            path: ':id',
            element: <RentalDetailPage />,
          },
          {
            path: 'history',
            element: <HistoryRentals />,
          },
          {
            path: 'calendar',
            element: <BookingCalendar />,
          },
          {
            // Where a freestyle rental may end. A fleet's admin and fleet
            // manager draw their own once Beast has switched freestyle on with
            // them; Beast staff pass both gates. See docs/RETURN_AREAS.md.
            path: 'return-areas',
            element: (
              <ProtectedRoute
                requiredRole={[...RETURN_AREA_ROLES]}
                requiredFeatureFlag={RETURN_AREA_FEATURE}
              >
                <ReturnAreasPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'feedback',
            element: <RentalsFeedbackPage />,
          },
          {
            // A place a rental starts and ends, so it lives with the rentals
            // rather than in Misc. Unchanged otherwise: the same gate it
            // carried there, because who may edit the pickup points did not
            // change when the screen moved. The static segment outranks the
            // `:id` sibling above it, so a rental id never swallows it.
            path: 'booking-locations',
            element: (
              <ProtectedRoute requiredRole={['admin', 'fleetManager']}>
                <BookingLocationsPage />
              </ProtectedRoute>
            ),
          },
        ],
      },

      {
        path: 'subscriptions',
        element: (
          <ProtectedRoute
            requiredFeatureFlag="subscriptionEnabled"
            requiredRole={['admin', 'fleetManager', 'fleetAssistant']}
          >
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '',
            element: <SubscriptionsListPage />,
          },
          {
            path: ':id',
            element: <SubscriptionPage />,
          },
          {
            path: 'vehicle-kinds',
            children: [
              {
                path: '',
                element: <VehicleKindsListPage />,
              },
              {
                path: ':id',
                element: <VehicleKindDetailPage />,
              },
              {
                path: 'create',
                element: (
                  <ProtectedRoute requiredRole={['admin', 'fleetManager']}>
                    <VehicleKindCreatePage />
                  </ProtectedRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'fleet-management',
        element: (
          <ProtectedRoute
            requiredFeatureFlag="fleetManagementEnabled"
            requiredRole={['admin', 'fleetManager', 'fleetAssistant']}
          >
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '',
            element: <OpsVehicleTasksListPage />,
          },
          {
            path: ':id',
            element: <OpsVehicleTaskPage />,
          },
        ],
      },
      {
        path: 'partners',
        element: (
          <ProtectedRoute requiredRole={[...P2P_ROLES]}>
            <Outlet />
          </ProtectedRoute>
        ),
        children: [
          {
            path: '',
            element: <PartnerListPage />,
          },
          {
            path: ':id',
            element: <PartnerPage />,
          },
          {
            path: 'applications',
            children: [
              {
                path: '',
                element: <VehicleApplicationListPage />,
              },
              {
                path: ':id',
                children: [
                  {
                    path: '',
                    element: <VehicleApplicationDetailsPage />,
                  },
                  {
                    path: 'edit',
                    element: <VehicleApplicationDetailsPage isEditing />,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'users',
        children: [
          {
            path: '',
            element: <UserListPage />,
          },
          {
            path: ':id',
            element: <UserDetailPage />,
          },
        ],
      },
      {
        path: 'b2b',
        children: [
          {
            path: '',
            element: <BusinessAccountsListPage />,
          },
          {
            path: ':id',
            element: <BusinessAccountDetailsPage />,
          },
        ],
      },
      {
        path: 'map',
        element: <VehicleMapPage />,
      },
      {
        path: 'misc',
        children: [
          {
            path: '',
            element: (
              <ProtectedRoute
                requiredFeatureFlag="marketingEnabled"
                requiredRole={['admin', 'fleetManager']}
              >
                <Navigate to="/admin/misc/marketing-campaigns" />
              </ProtectedRoute>
            ),
          },
          {
            path: 'app-config',
            element: (
              // Beast staff only. App config is one row for the whole app, so
              // a provider switching Promo codes off would switch it off for
              // every renter; the backend refuses the write to anybody else,
              // and the route matches it so a provider who knows the path sees
              // a 403 rather than a form whose Save fails.
              <ProtectedRoute requiredRole={['superAdmin']}>
                <AppConfigPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'analytics-report',
            element: (
              <ProtectedRoute
                requiredFeatureFlag="analyticsEnabled"
                requiredRole={['admin', 'fleetManager']}
              >
                <AnalyticsPage />
              </ProtectedRoute>
            ),
          },
          {
            // What each car earns and costs by the km and the month
            // (docs/AMORTISATION.md), behind the switch General reports is.
            path: 'amortisation',
            element: (
              <ProtectedRoute
                requiredFeatureFlag="analyticsEnabled"
                requiredRole={['admin', 'fleetManager']}
              >
                <AmortisationPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'costs',
            element: (
              // Beast staff only: Beast's own cost base. The backend refuses
              // every costs field to anybody else, and the route matches it
              // so a provider who knows the path sees a 403 rather than a page
              // of GraphQL errors.
              <ProtectedRoute requiredRole={[...COSTS_ROLES]}>
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              { path: '', element: <CostsPage /> },
              { path: ':vendor', element: <CostVendorPage /> },
            ],
          },
          {
            path: 'business',
            element: (
              // Beast staff only, as the Costs pages it is made from: the
              // backend refuses `businessOverview` to anybody else.
              <ProtectedRoute requiredRole={[...BUSINESS_ROLES]}>
                <BusinessPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'marketing-campaigns',
            element: (
              <ProtectedRoute
                requiredFeatureFlag="marketingEnabled"
                requiredRole={['admin', 'fleetManager']}
              >
                <MarketingCampaignsPage />
              </ProtectedRoute>
            ),
          },
          {
            // The second tab, as a path rather than as component state.
            //
            // A tab kept in `useState` is a screen with no address: it cannot
            // be linked to, it does not survive a reload, and `preview:shots`
            // opened this page fifty times and only ever saw the first tab,
            // so half of it was outside every sweep. A sibling path rather
            // than a `children` array, because the page renders no `Outlet`
            // and a parent with children stops being a leaf the route drift
            // test can see.
            path: 'marketing-campaigns/notifications',
            element: (
              <ProtectedRoute
                requiredFeatureFlag="marketingEnabled"
                requiredRole={['admin', 'fleetManager']}
              >
                <MarketingCampaignsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'promo-codes',
            element: (
              <ProtectedRoute
                requiredFeatureFlag="promoCodeEnabled"
                requiredRole={['admin', 'fleetManager']}
              >
                <PromoCodesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'pricing',
            element: (
              <ProtectedRoute requiredRole={['admin', 'fleetManager']}>
                <PricingPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'rental-terms',
            element: (
              <ProtectedRoute requiredRole={['admin', 'fleetManager']}>
                <RentalTermsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'partner-pricing',
            element: (
              <ProtectedRoute requiredRole={[...P2P_ROLES]}>
                <PartnerPricingTiersPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'verification-reviews',
            element: (
              // Beast staff, and a fleet's admins and fleet managers once Beast
              // has given the fleet "Decide licence reviews": see
              // `src/access/kycReviews`. The backend holds the same rule.
              <ProtectedRoute
                requiredRole={[...KYC_REVIEW_ACCESS.requiredRole]}
                requiredFeatureFlag={KYC_REVIEW_ACCESS.requiredFeatureFlag}
              >
                <Outlet />
              </ProtectedRoute>
            ),
            children: [
              { path: '', element: <KycReviewsPage /> },
              { path: ':id', element: <KycReviewPage /> },
            ],
          },
          {
            path: 'user-remove-requests',
            element: (
              // Beast staff only. The backend refuses this query to anybody
              // else; the route matches it so a provider who knows the path
              // sees a 403 rather than an empty screen full of GraphQL errors.
              <ProtectedRoute requiredRole={[...ERASURE_ROLES]}>
                <UserDeleteRequestPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'report-exports',
            element: (
              <ProtectedRoute
                requiredFeatureFlag="reportExportsEnabled"
                requiredRole={['admin', 'fleetManager']}
              >
                <ReportExportsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'vehicle-pictures',
            element: (
              <ProtectedRoute
                requiredFeatureFlag="vehiclePicturesEnabled"
                requiredRole={['admin', 'fleetManager']}
              >
                <VehiclePicturesPage />
              </ProtectedRoute>
            ),
          },
          {
            // The screen moved under Rentals; the address it spent a year at
            // still answers. A bookmark, a link in a handover note and the
            // setup checklist someone left open all point here, and a path
            // that quietly 404s is the worst version of a move. It is a
            // `<Navigate>` rather than a second mount, so there is one screen
            // with one address and the preview manifest does not gain a
            // duplicate: `routes.test.ts` reads a redirect as not a screen.
            path: 'booking-locations',
            element: <Navigate to="/admin/rentals/booking-locations" replace />,
          },
          {
            path: 'tags',
            element: (
              <ProtectedRoute requiredRole={['admin', 'fleetManager']}>
                <TagsPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'managers',
            element: (
              // Admin, as the API is: `managers` and `managerCreate` refuse
              // anybody else. Open to fleet managers, the screen read "No team
              // members yet" and its button failed with Forbidden.
              <ProtectedRoute requiredRole={['admin']}>
                <ManagersPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'logout',
        element: <LogoutPage />,
      },
    ],
  },
  {
    path: '*',
    element: <LogoOnlyLayout />,
    children: [
      { path: '403', element: <Forbidden /> },
      { path: '404', element: <NotFound /> },
      { path: '*', element: <Navigate to="/404" replace /> },
    ],
  },
  {
    path: '/',
    element: <Navigate to="/admin" replace />,
    children: [],
  },
  { path: '*', element: <Navigate to="/404" replace /> },
]

export default function Router() {
  return useRoutes(routeConfig)
}
